import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './store/authContext';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import Login from './pages/auth/Login';
import Dashboard from './pages/dashboard/Dashboard';
import LeadList from './pages/leads/LeadList';
import LeadDetail from './pages/leads/LeadDetail';
import UserList from './pages/users/UserList';
import Settings from './pages/settings/Settings';
import Profile from './pages/profile/Profile';
import SalesDashboard from './pages/sales/SalesDashboard';
import SaleList from './pages/sales/SaleList';
import SaleCreate from './pages/sales/SaleCreate';
import SaleDetail from './pages/sales/SaleDetail';
import SuperAdminTenants from './pages/super-admin/Tenants';
import TenantDetail from './pages/super-admin/TenantDetail';
import TenantUsers from './pages/super-admin/TenantUsers';

function ProtectedRoute({ children, roles }) {
  const { isAuthenticated, user, loading } = useAuth();
  if (loading) return <div className="app-loading" />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user?.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} />

      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="leads" element={
          <ProtectedRoute roles={['admin', 'agent']}>
            <LeadList />
          </ProtectedRoute>
        } />
        <Route path="leads/:id" element={
          <ProtectedRoute roles={['admin', 'agent']}>
            <LeadDetail />
          </ProtectedRoute>
        } />
        <Route path="sales" element={
          <ProtectedRoute roles={['admin', 'agent']}>
            <SalesDashboard />
          </ProtectedRoute>
        } />
        <Route path="sales/all" element={
          <ProtectedRoute roles={['admin', 'agent']}>
            <SaleList />
          </ProtectedRoute>
        } />
        <Route path="sales/new" element={
          <ProtectedRoute roles={['admin', 'agent']}>
            <SaleCreate />
          </ProtectedRoute>
        } />
        <Route path="sales/:id" element={
          <ProtectedRoute roles={['admin', 'agent']}>
            <SaleDetail />
          </ProtectedRoute>
        } />
        <Route path="users" element={
          <ProtectedRoute roles={['admin']}>
            <UserList />
          </ProtectedRoute>
        } />
        <Route path="settings" element={
          <ProtectedRoute roles={['admin']}>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="profile" element={
          <ProtectedRoute roles={['agent', 'super_admin']}>
            <Profile />
          </ProtectedRoute>
        } />
        <Route path="super-admin/tenants" element={
          <ProtectedRoute roles={['super_admin']}>
            <SuperAdminTenants />
          </ProtectedRoute>
        } />
        <Route path="super-admin/tenants/:tenantId" element={
          <ProtectedRoute roles={['super_admin']}>
            <TenantDetail />
          </ProtectedRoute>
        } />
        <Route path="super-admin/tenants/:tenantId/users" element={
          <ProtectedRoute roles={['super_admin']}>
            <TenantUsers />
          </ProtectedRoute>
        } />
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
